import { useState, useEffect, useRef } from "react";
import { OnboardingTour } from "./OnboardingTour";
import { UtcClock } from "./UtcClock";
import { Link, useLocation } from "wouter";
import { LayoutDashboard, Crosshair, Target, Shield, LogOut, Menu, X, BookOpen, LifeBuoy, Settings, ScrollText } from "lucide-react";
import logoUrl from "../assets/logo.png";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth";
import { useTrack, TRACK_INFO, type LicenseTrack } from "@/contexts/track";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/language";

function isDaytime(): boolean {
  const h = new Date().getHours() + new Date().getMinutes() / 60;
  const m = new Date().getMonth();
  const dawn = [7.5,7.0,6.25,5.5,4.75,4.5,5.0,5.5,6.25,7.0,7.5,7.75][m];
  const dusk = [17.0,17.75,18.5,19.5,20.25,21.25,21.25,20.75,19.75,18.5,17.25,16.75][m];
  return h >= dawn && h < dusk;
}

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = { href: string; label: string; icon: React.ElementType; badge?: number; towerLight?: boolean; pendingAlert?: boolean };


const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

type SidebarCounts = {
  unread: number;
  ticketUnread: number;
  pendingCount: number;
  newRegistrationsCount: number;
  requireApproval: boolean;
  flagCount: number;
  publicContactCount: number;
  lockedCount: number;
  pendingPushVoteCount: number;
};
const EMPTY_SIDEBAR_COUNTS: SidebarCounts = {
  unread: 0,
  ticketUnread: 0,
  pendingCount: 0,
  newRegistrationsCount: 0,
  requireApproval: true,
  flagCount: 0,
  publicContactCount: 0,
  lockedCount: 0,
  pendingPushVoteCount: 0,
};

async function authedGet(path: string): Promise<any> {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  return res.json();
}

async function getSidebarCounts(): Promise<SidebarCounts> {
  const data = await authedGet(`/api/sidebar-counts`);
  return {
    unread: data?.unread ?? 0,
    ticketUnread: data?.ticketUnread ?? 0,
    pendingCount: data?.pendingCount ?? 0,
    newRegistrationsCount: data?.newRegistrationsCount ?? 0,
    requireApproval: data?.requireApproval !== false,
    flagCount: data?.flagCount ?? 0,
    publicContactCount: data?.publicContactCount ?? 0,
    lockedCount: data?.lockedCount ?? 0,
    pendingPushVoteCount: data?.pendingPushVoteCount ?? 0,
  };
}

function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission().catch(() => {});
  }
}

async function safeNotify(title: string, options: NotificationOptions) {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    new Notification(title, options);
    return;
  } catch (_err) {
    // Some browsers/PWA contexts block Notification constructor.
  }
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.();
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
    }
  } catch {
    // Never allow notification failures to crash UI flows.
  }
}

function firePendingNotification(count: number) {
  if (document.visibilityState === "visible") return;
  void safeNotify("Flight Deck — Pending Approval", {
    body: `${count} pilot${count !== 1 ? "s" : ""} waiting for account approval.`,
    icon: "/favicon.ico",
    tag: "pending-users",
  });
}

function fireNewRegistrationNotification(count: number) {
  if (document.visibilityState === "visible") return;
  void safeNotify("Flight Deck — New registrations", {
    body: `${count} new pilot${count !== 1 ? "s" : ""} registered.`,
    icon: "/favicon.ico",
    tag: "new-registrations",
  });
}

function fireTicketNotification(newCount: number, prevCount: number) {
  const added = newCount - prevCount;
  if (added <= 0) return;
  void safeNotify("Flight Deck — New Support Ticket", {
    body: added === 1
      ? "A new support ticket is waiting for a response."
      : `${added} new support tickets need attention.`,
    icon: "/favicon.ico",
    tag: "ticket-unread",
  });
}

function firePublicContactNotification(added: number) {
  void safeNotify("Flight Deck — New Support Message", {
    body: added === 1
      ? "Someone submitted a support message from the login page."
      : `${added} new support messages from the login page.`,
    icon: "/favicon.ico",
    tag: "public-contacts",
  });
}

function ProgrammeTrackSelect() {
  const { selectedTrack, setSelectedTrack, enabledTracksList } = useTrack();
  return (
    <div className="px-4 py-2.5 border-b border-border/50 shrink-0">
      <span className="block text-[9px] font-mono text-muted-foreground/50 uppercase tracking-widest mb-1.5">Study programme</span>
      <Select value={selectedTrack} onValueChange={(v) => setSelectedTrack(v as LicenseTrack)}>
        <SelectTrigger className="h-9 text-xs font-mono bg-background/80 border-border">
          <SelectValue placeholder="CPL" />
        </SelectTrigger>
        <SelectContent>
          {enabledTracksList.map((id) => (
            <SelectItem key={id} value={id} className="text-xs font-mono">
              {TRACK_INFO[id].label} — {TRACK_INFO[id].fullName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function fireLockedNotification(count: number) {
  if (document.visibilityState === "visible") return;
  void safeNotify("Flight Deck — Account Locked", {
    body: count === 1
      ? "A user account has been locked. Check the Admin Panel."
      : `${count} user accounts are currently locked.`,
    icon: "/favicon.ico",
    tag: "locked-users",
  });
}

// ── Glowing tower light indicator ─────────────────────────────────────────────
function TowerLight({ unread }: { unread: boolean }) {
  if (!unread) return null;
  return (
    <span
      className="relative flex h-2 w-2 shrink-0"
      title="Unread messages"
    >
      {/* Animated ping ring */}
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff00] opacity-60" />
      {/* Solid core */}
      <span
        className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff00]"
        style={{ boxShadow: "0 0 6px 2px rgba(0,255,0,0.55)" }}
      />
    </span>
  );
}

export function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const { user, logout, needs2faSetup } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const { t, language, setLanguage } = useLanguage();

  const normalizedRole = String(user?.role ?? "").trim().toLowerCase();
  const isAdminOrMod = ["admin", "master_admin", "moderator"].includes(normalizedRole);
  const isAdmin = ["admin", "master_admin"].includes(normalizedRole);
  const { toast } = useToast();

  // ── Single consolidated sidebar-counts query ──
  const { data: sidebarCounts = EMPTY_SIDEBAR_COUNTS } = useQuery<SidebarCounts>({
    queryKey: ["sidebar-counts"],
    queryFn: getSidebarCounts,
    staleTime: isAdminOrMod ? 10_000 : 30_000,
    refetchInterval: isAdminOrMod ? 15_000 : 60_000,
    enabled: !!user,
  });
  const {
    unread: unreadCount,
    ticketUnread,
    pendingCount,
    newRegistrationsCount,
    requireApproval,
    flagCount,
    publicContactCount,
    lockedCount,
    pendingPushVoteCount,
  } = sidebarCounts;
  const registrationAlertCount = requireApproval ? pendingCount : newRegistrationsCount;

  // Request notification permission as soon as admin or mod is logged in
  useEffect(() => {
    if (isAdminOrMod) requestNotificationPermission();
  }, [isAdminOrMod]);

  // Fire a browser notification when the pending count increases (approval workflow on)
  const prevPendingRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdmin || !requireApproval) return;
    if (prevPendingRef.current === null) {
      prevPendingRef.current = pendingCount;
      return;
    }
    if (pendingCount > prevPendingRef.current) {
      const added = pendingCount - prevPendingRef.current;
      toast({
        title: added === 1 ? "New Pilot Awaiting Approval" : `${added} Pilots Awaiting Approval`,
        description: added === 1
          ? "A new pilot has registered and needs account approval. Check the Admin Panel."
          : `${added} new pilots have registered and need account approval.`,
        duration: 9000,
      });
      firePendingNotification(pendingCount);
    }
    prevPendingRef.current = pendingCount;
  }, [pendingCount, requireApproval, isAdmin, toast]);

  const prevNewRegRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdmin || requireApproval) return;
    if (prevNewRegRef.current === null) {
      prevNewRegRef.current = newRegistrationsCount;
      return;
    }
    if (newRegistrationsCount > prevNewRegRef.current) {
      const added = newRegistrationsCount - prevNewRegRef.current;
      toast({
        title: added === 1 ? "New Registration" : `${added} New Registrations`,
        description: added === 1
          ? "A new pilot registered (auto-approved). Check the Admin Panel."
          : `${added} new pilots registered (auto-approved). Check the Admin Panel.`,
        duration: 9000,
      });
      fireNewRegistrationNotification(newRegistrationsCount);
    }
    prevNewRegRef.current = newRegistrationsCount;
  }, [newRegistrationsCount, requireApproval, isAdmin, toast]);

  // Fire notifications when new support tickets arrive
  const prevTicketRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdminOrMod) return;
    if (prevTicketRef.current === null) {
      // First load — just store the baseline, don't fire
      prevTicketRef.current = ticketUnread;
      return;
    }
    if (ticketUnread > prevTicketRef.current) {
      const added = ticketUnread - prevTicketRef.current;
      // Always fire in-app toast (works even when tab is active)
      toast({
        title: added === 1 ? "New Support Ticket" : `${added} New Support Tickets`,
        description: added === 1
          ? "A user has submitted a support ticket. Check Support Tickets."
          : `${added} new support tickets need your attention.`,
        duration: 8000,
      });
      // Also fire browser notification when tab is in background
      fireTicketNotification(ticketUnread, prevTicketRef.current);
    }
    prevTicketRef.current = ticketUnread;
  }, [ticketUnread, isAdminOrMod, toast]);

  // Fire notification when a push vote is waiting for this admin's review
  const prevPushVoteRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdmin) return;
    if (prevPushVoteRef.current === null) {
      prevPushVoteRef.current = pendingPushVoteCount;
      return;
    }
    if (pendingPushVoteCount > prevPushVoteRef.current) {
      const added = pendingPushVoteCount - prevPushVoteRef.current;
      toast({
        title: added === 1 ? "✅ Push Vote Needed" : `✅ ${added} Push Votes Needed`,
        description: added === 1
          ? "A pending item needs your push vote before it can move forward in the admin workflow."
          : `${added} pending items need your push vote in the admin workflow.`,
        duration: 10000,
      });
      void safeNotify("Push Vote Needed", {
        body: `${added} pending item${added !== 1 ? "s" : ""} need${added === 1 ? "s" : ""} your push approval.`,
        icon: "/icons/icon-192.png",
      });
    }
    prevPushVoteRef.current = pendingPushVoteCount;
  }, [pendingPushVoteCount, isAdmin, toast]);

  // Fire notifications when new public support messages arrive (from the login page form)
  const prevPublicContactRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdminOrMod) return;
    if (prevPublicContactRef.current === null) {
      prevPublicContactRef.current = publicContactCount;
      if (publicContactCount > 0) {
        toast({
          title: publicContactCount === 1 ? "📬 Unread Pre-Login Message" : `📬 ${publicContactCount} Unread Pre-Login Messages`,
          description: publicContactCount === 1
            ? "There is 1 unread pre-login support message waiting. Check Support Tickets."
            : `There are ${publicContactCount} unread pre-login messages waiting. Check Support Tickets.`,
          duration: 9000,
        });
      }
      return;
    }
    if (publicContactCount > prevPublicContactRef.current) {
      const added = publicContactCount - prevPublicContactRef.current;
      toast({
        title: added === 1 ? "📬 New Support Message" : `📬 ${added} New Support Messages`,
        description: added === 1
          ? "Someone submitted a support message from the login page. Check Support Tickets."
          : `${added} new support messages from the login page need your attention.`,
        duration: 9000,
      });
      firePublicContactNotification(added);
    }
    prevPublicContactRef.current = publicContactCount;
  }, [publicContactCount, isAdminOrMod, toast]);

  // Fire notification when accounts become locked
  const prevLockedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAdmin) return;
    if (prevLockedRef.current === null) {
      prevLockedRef.current = lockedCount;
      return;
    }
    if (lockedCount > prevLockedRef.current) {
      const added = lockedCount - prevLockedRef.current;
      toast({
        title: added === 1 ? "🔒 Account Locked" : `🔒 ${added} Accounts Locked`,
        description: added === 1
          ? "A user account has been locked. Check the Admin Panel."
          : `${added} user accounts are now locked.`,
        duration: 9000,
      });
      fireLockedNotification(lockedCount);
    }
    prevLockedRef.current = lockedCount;
  }, [lockedCount, isAdmin, toast]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const cadorsNavOn = import.meta.env.VITE_ENABLE_CADORS === "1";
  const studyNavItems: NavItem[] = [
    { href: "/", label: t.nav.flightDeck, icon: LayoutDashboard },
    { href: "/practice", label: t.nav.requestTakeoff, icon: Crosshair },
    { href: "/weaknesses", label: t.nav.turbulenceZones, icon: Target },
    ...(cadorsNavOn ? [{ href: "/cadors", label: t.nav.cadorsDaily, icon: ScrollText } as NavItem] : []),
  ];

  const roleNavItems: NavItem[] = [
    ...(normalizedRole === "moderator" ? [
      { href: "/moderator/tickets", label: t.nav.supportTickets, icon: LifeBuoy, ...(ticketUnread > 0 ? { badge: ticketUnread, towerLight: true } : {}) },
      { href: "/moderator/team", label: t.nav.modTeam, icon: Shield },
      { href: "/admin/questions", label: t.nav.questionReview, icon: BookOpen },
    ] : []),
    ...(["admin", "master_admin"].includes(normalizedRole) ? [
      { href: "/admin", label: t.nav.adminPanel, icon: Shield, ...((registrationAlertCount + lockedCount) > 0 ? { badge: registrationAlertCount + lockedCount, pendingAlert: registrationAlertCount > 0 } : {}) },
      { href: "/admin/questions", label: t.nav.questionBank, icon: BookOpen, ...(flagCount > 0 ? { badge: flagCount } : {}) },
      { href: "/admin/tickets", label: t.nav.supportTickets, icon: LifeBuoy, ...((ticketUnread + publicContactCount) > 0 ? { badge: ticketUnread + publicContactCount, towerLight: true } : {}) },
    ] : []),
  ];

  const commNavItems: NavItem[] = [
    { href: "/contact", label: t.nav.tower, icon: LifeBuoy, towerLight: unreadCount > 0 },
  ];

  // Mandatory 2FA block — admins and mods must set up 2FA before accessing anything
  // Allow /settings so they can complete the setup
  if (needs2faSetup && isAdminOrMod && location !== "/settings") {
    const bgImg = isDaytime() ? bgDayUrl : bgNightUrl;
    return (
      <div
        className="flex h-[100dvh] w-full items-center justify-center p-4 relative"
        style={{ backgroundImage: `url(${bgImg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl p-8 text-center space-y-4">
            <img src={logoUrl} alt="Flight Deck" className="h-8 w-auto mx-auto mb-2" />
            <div className="bg-amber-500/20 p-3 rounded-full w-14 h-14 mx-auto flex items-center justify-center">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold font-mono text-white tracking-wide">{t.twoFa.title}</h2>
            <p className="text-sm text-white/70">{t.twoFa.body}</p>
            <a
              href={`${BASE_URL}/settings#two-factor-auth`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold font-mono text-sm rounded-md transition-colors"
            >
              <Shield className="w-4 h-4" /> {t.twoFa.cta}
            </a>
            <div className="pt-2">
              <button
                onClick={logout}
                className="text-xs font-mono text-white/40 hover:text-white/70 transition-colors flex items-center gap-1 mx-auto"
              >
                <LogOut className="w-3 h-3" /> {t.nav.signOutInstead}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] w-full bg-background text-foreground overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="w-64 border-r border-sidebar-border bg-sidebar flex-col hidden md:flex shrink-0 overflow-hidden">
        <div className="px-4 py-4 flex items-center gap-3 border-b border-border/50">
          <div className="flex-1 min-w-0">
            <img src={logoUrl} alt="Flight Deck" className="h-36 w-auto max-w-full" />
          </div>
          <button
            onClick={() => setLanguage(language === "en" ? "fr" : "en")}
            className="shrink-0 text-[11px] font-bold font-mono px-2 py-1 rounded border border-primary/40 text-primary hover:bg-primary/15 transition-colors"
            title={t.switchTo}
          >
            {language === "en" ? "FR" : "EN"}
          </button>
        </div>

        <ProgrammeTrackSelect />

        <nav className="flex-1 px-4 py-2 flex flex-col overflow-y-auto">
          {/* Study items */}
          <div className="space-y-1">
            {studyNavItems.map((item) => {
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.towerLight && <TowerLight unread={true} />}
                  {item.badge !== undefined && (
                    <span className={cn(
                      "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                      item.pendingAlert
                        ? "bg-yellow-400 text-yellow-900 animate-pulse"
                        : "bg-primary text-primary-foreground"
                    )}>
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Role-specific items (admin/mod) */}
          {roleNavItems.length > 0 && (
            <>
              <div className="my-3 border-t border-border/50" />
              <div className="space-y-1">
                {roleNavItems.map((item) => {
                  const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.towerLight && <TowerLight unread={true} />}
                      {item.badge !== undefined && (
                        <span className={cn(
                          "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                          item.pendingAlert
                            ? "bg-yellow-400 text-yellow-900 animate-pulse"
                            : "bg-primary text-primary-foreground"
                        )}>
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {/* Spacer — pushes comms to the bottom */}
          <div className="flex-1" />

          {/* Communications — always at the bottom */}
          <div className="pb-1">
            <div className="mb-2 px-3 flex items-center gap-2">
              <div className="flex-1 border-t border-border/50" />
              <span className="text-[9px] font-mono text-muted-foreground/40 uppercase tracking-widest shrink-0">Comms</span>
              <div className="flex-1 border-t border-border/50" />
            </div>
            <div className="space-y-1">
              {commNavItems.map((item) => {
                const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.towerLight && <TowerLight unread={true} />}
                    {item.badge !== undefined && (
                      <span className={cn(
                        "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                        item.pendingAlert
                          ? "bg-yellow-400 text-yellow-900 animate-pulse"
                          : "bg-primary text-primary-foreground"
                      )}>
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-border">
          <UtcClock />
          <Link
            href="/settings"
            className={cn(
              "flex items-center gap-3 mb-2 px-2 py-2 rounded-md transition-colors group",
              location === "/settings"
                ? "bg-primary/10"
                : "hover:bg-secondary"
            )}
          >
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <span className="text-primary text-xs font-bold font-mono">
                {user?.fullName?.charAt(0).toUpperCase() ?? "?"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className={cn("text-xs font-medium truncate", location === "/settings" ? "text-primary" : "text-foreground")}>{user?.fullName}</div>
              <div className="text-xs text-muted-foreground/60 font-mono truncate">{user?.email}</div>
            </div>
            <Settings className={cn("h-3.5 w-3.5 shrink-0 transition-colors", location === "/settings" ? "text-primary" : "text-muted-foreground/40 group-hover:text-muted-foreground")} />
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={logout}
              className="flex items-center gap-2 flex-1 px-3 py-2 rounded-md text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors font-mono"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t.nav.signOut.toUpperCase()}
            </button>
            <button
              onClick={() => setLanguage(language === "en" ? "fr" : "en")}
              className="flex items-center justify-center px-2.5 py-2 rounded-md text-xs font-bold font-mono border border-primary/30 text-primary/70 hover:bg-primary/10 hover:text-primary transition-colors shrink-0"
              title={t.switchTo}
            >
              {language === "en" ? "FR" : "EN"}
            </button>
          </div>
          <div className="mt-1 flex gap-3 px-1">
            <Link href="/privacy" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Privacy</Link>
            <Link href="/terms" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Terms</Link>
            <Link href="/waiver" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Waiver</Link>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={cn(
          "md:hidden fixed top-0 left-0 z-50 h-full w-72 bg-sidebar border-r border-sidebar-border flex flex-col transition-transform duration-300 ease-in-out mobile-nav-sidebar",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="p-5 flex items-center justify-between border-b border-border">
          <div className="flex-1 min-w-0 mr-3">
            <img src={logoUrl} alt="Flight Deck" className="h-36 w-auto max-w-[300px]" />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ProgrammeTrackSelect />

        <nav className="flex-1 px-4 py-3 flex flex-col overflow-y-auto">
          {/* Study items */}
          <div className="space-y-1">
            {studyNavItems.map((item) => {
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.towerLight && <TowerLight unread={true} />}
                  {item.badge !== undefined && (
                    <span className={cn(
                      "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                      item.pendingAlert
                        ? "bg-yellow-400 text-yellow-900 animate-pulse"
                        : "bg-primary text-primary-foreground"
                    )}>
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Role-specific items */}
          {roleNavItems.length > 0 && (
            <>
              <div className="my-3 border-t border-border/50" />
              <div className="space-y-1">
                {roleNavItems.map((item) => {
                  const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.towerLight && <TowerLight unread={true} />}
                      {item.badge !== undefined && (
                        <span className={cn(
                          "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                          item.pendingAlert
                            ? "bg-yellow-400 text-yellow-900 animate-pulse"
                            : "bg-primary text-primary-foreground"
                        )}>
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {/* Spacer — pushes comms to the bottom */}
          <div className="flex-1" />

          {/* Communications — always at the bottom */}
          <div className="pb-1">
            <div className="mb-2 px-3 flex items-center gap-2">
              <div className="flex-1 border-t border-border/50" />
              <span className="text-[9px] font-mono text-muted-foreground/40 uppercase tracking-widest shrink-0">Comms</span>
              <div className="flex-1 border-t border-border/50" />
            </div>
            <div className="space-y-1">
              {commNavItems.map((item) => {
                const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.towerLight && <TowerLight unread={true} />}
                    {item.badge !== undefined && (
                      <span className={cn(
                        "ml-auto text-[10px] font-mono font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1",
                        item.pendingAlert
                          ? "bg-yellow-400 text-yellow-900 animate-pulse"
                          : "bg-primary text-primary-foreground"
                      )}>
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-border">
          <UtcClock />
          <Link
            href="/settings"
            className={cn(
              "flex items-center gap-3 mb-2 px-2 py-2 rounded-md transition-colors group",
              location === "/settings"
                ? "bg-primary/10"
                : "hover:bg-secondary"
            )}
          >
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <span className="text-primary text-sm font-bold font-mono">
                {user?.fullName?.charAt(0).toUpperCase() ?? "?"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className={cn("text-sm font-medium truncate", location === "/settings" ? "text-primary" : "text-foreground")}>{user?.fullName}</div>
              <div className="text-xs text-muted-foreground/60 font-mono truncate">{user?.email}</div>
            </div>
            <Settings className={cn("h-4 w-4 shrink-0 transition-colors", location === "/settings" ? "text-primary" : "text-muted-foreground/40 group-hover:text-muted-foreground")} />
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={logout}
              className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors font-mono"
            >
              <LogOut className="h-4 w-4" />
              {t.nav.signOut.toUpperCase()}
            </button>
            <button
              onClick={() => setLanguage(language === "en" ? "fr" : "en")}
              className="flex items-center justify-center px-3 py-2.5 rounded-md text-sm font-bold font-mono border border-primary/30 text-primary/70 hover:bg-primary/10 hover:text-primary transition-colors shrink-0"
              title={t.switchTo}
            >
              {language === "en" ? "FR" : "EN"}
            </button>
          </div>
          <div className="mt-3 flex gap-3 px-1">
            <Link href="/privacy" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Privacy</Link>
            <Link href="/terms" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Terms</Link>
            <Link href="/waiver" className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors font-mono">Waiver</Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 border-b border-sidebar-border bg-sidebar shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex items-center gap-2"
            aria-label={t.nav.openMenu}
          >
            <Menu className="h-5 w-5 text-muted-foreground" />
            <img src={logoUrl} alt="Flight Deck" className="h-[60px] w-auto overflow-hidden object-left object-cover" />
          </button>
          <div className="flex items-center gap-2">
            <UtcClock compact />
            <button
              onClick={() => setLanguage(language === "en" ? "fr" : "en")}
              className="text-[10px] font-bold font-mono px-2 py-1 rounded border border-primary/30 text-primary/70 hover:bg-primary/10 hover:text-primary transition-colors"
              title={t.switchTo}
            >
              {language === "en" ? "FR" : "EN"}
            </button>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t.nav.openNav}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto min-h-full">
            {/* Password Expiry Banner */}
            {(() => {
              if (!user?.passwordChangedAt) return null;
              const ageMs = Date.now() - new Date(user.passwordChangedAt).getTime();
              const ageDays = ageMs / (1000 * 60 * 60 * 24);
              if (ageDays < 90) return null;
              return (
                <div className="mb-4 border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-amber-400 font-mono text-xs tracking-wide flex items-start gap-3">
                  <span className="text-base leading-tight shrink-0">⚠</span>
                  <div>
                    <span className="font-bold">PASSWORD EXPIRY WARNING — </span>
                    Your password is {Math.floor(ageDays)} days old. Please{" "}
                    <a href="/profile" className="underline underline-offset-2 hover:text-amber-300 transition-colors">
                      update your password
                    </a>{" "}
                    to maintain account security.
                  </div>
                </div>
              );
            })()}
            {children}
          </div>
        </div>
      </main>
      <OnboardingTour />
    </div>
  );
}
